import { type SchedulePushKey, schedulePushKey } from "./schedulePushConsts";

/**
 * Translation keys for the preview's counts. The backend sends the planner's own action and skip-reason names; each
 * one is worded here for what a support user sees in the vendor. HostedTime's come from
 * `backend/services/hostedtime_schedule_diff.py` (`ActionType`, `SkipReason`).
 */

export const PREVIEW_ACTION_KEYS: Readonly<Record<string, SchedulePushKey>> = {
	create: schedulePushKey("preview.action.create"),
	update: schedulePushKey("preview.action.update"),
	delete: schedulePushKey("preview.action.delete"),
	takeover: schedulePushKey("preview.action.takeover"),
	mark_off_day: schedulePushKey("preview.action.markOffDay"),
	adopt_existing: schedulePushKey("preview.action.adoptExisting"),
};

export const PREVIEW_SKIP_KEYS: Readonly<Record<string, SchedulePushKey>> = {
	missing_employee_oid: schedulePushKey("preview.skip.missingEmployee"),
	ambiguous_employee_oid: schedulePushKey("preview.skip.ambiguousEmployee"),
	unresolved_unit: schedulePushKey("preview.skip.unresolvedUnit"),
	unmapped_unit: schedulePushKey("preview.skip.unmappedUnit"),
	unresolved_position: schedulePushKey("preview.skip.unresolvedPosition"),
	employee_absent_from_roster: schedulePushKey("preview.skip.absentFromRoster"),
	vendor_accepted_but_no_row: schedulePushKey("preview.skip.acceptedButMissing"),
	zero_duration: schedulePushKey("preview.skip.zeroDuration"),
};

/**
 * Attendance on Demand's: a write is one employee's whole day (`schedule_push/contract.py` `ActionType`), and a shift
 * is held back for a reason about the shift or about the day it falls on (`backend/services/aod_schedule_types.py`
 * `SkipReason` — a held day counts each of its shifts under the day's reason).
 */
export const AOD_PREVIEW_ACTION_KEYS: Readonly<Record<string, SchedulePushKey>> = {
	create: schedulePushKey("aod.preview.action.create"),
	update: schedulePushKey("aod.preview.action.update"),
	delete: schedulePushKey("aod.preview.action.delete"),
	adopt_existing: schedulePushKey("aod.preview.action.adoptExisting"),
};

export const AOD_PREVIEW_SKIP_KEYS: Readonly<Record<string, SchedulePushKey>> = {
	missing_employee_id: schedulePushKey("aod.preview.skip.missingEmployeeId"),
	ambiguous_employee_id: schedulePushKey("aod.preview.skip.ambiguousEmployeeId"),
	shared_employee_id: schedulePushKey("aod.preview.skip.sharedEmployeeId"),
	employee_not_in_aod_roster: schedulePushKey("aod.preview.skip.notInRoster"),
	terminated_in_aod: schedulePushKey("aod.preview.skip.terminated"),
	zero_or_negative_duration: schedulePushKey("aod.preview.skip.zeroDuration"),
	overlapping_shifts: schedulePushKey("aod.preview.skip.overlapping"),
	unresolved_business_unit: schedulePushKey("aod.preview.skip.unresolvedBusinessUnit"),
	day_blocked_by_other_shift: schedulePushKey("aod.preview.skip.dayBlocked"),
	absence_on_day: schedulePushKey("aod.preview.skip.absence"),
	foreign_work_on_day: schedulePushKey("aod.preview.skip.foreignWork"),
	department_not_in_scope: schedulePushKey("aod.preview.skip.departmentNotSending"),
	unmapped_ledger_employee: schedulePushKey("aod.preview.skip.unmappedLedgerEmployee"),
	unresolved_claim: schedulePushKey("aod.preview.skip.unresolvedClaim"),
	read_back_mismatch: schedulePushKey("aod.preview.skip.readBackMismatch"),
	vendor_rejected_repeatedly: schedulePushKey("aod.preview.skip.rejectedRepeatedly"),
};

/** A reason this table does not know yet still has to be counted, so it is shown under one honest label. */
export const PREVIEW_SKIP_UNKNOWN_KEY = schedulePushKey("preview.skip.other");
export const PREVIEW_ACTION_UNKNOWN_KEY = schedulePushKey("preview.action.other");

export type PreviewCountLine = { id: string; labelKey: SchedulePushKey; count: number };

/**
 * One line per non-zero count, largest first. An unknown name is kept as its own line under the "other" label
 * rather than dropped: a skip nobody can see is a shift silently missing from the customer's schedule.
 */
export const previewCountLines = (
	counts: Readonly<Record<string, number>>,
	keys: Readonly<Record<string, SchedulePushKey>>,
	unknownKey: SchedulePushKey,
): PreviewCountLine[] =>
	Object.entries(counts)
		.filter(([, count]) => count > 0)
		// DEFAULT: a name added to the planner after this list was written still reaches the user, as "other".
		.map(([id, count]) => ({ id, labelKey: keys[id] ?? unknownKey, count }))
		.sort((first, second) => second.count - first.count || first.id.localeCompare(second.id));

export const sumCounts = (counts: Readonly<Record<string, number>>): number => Object.values(counts).reduce((sum, count) => sum + count, 0);
